import {IProject} from "../Components/Hub/Interfaces/IAllProjects";
import {GlobalDictionary} from "./globalDictionary";
import {IModule} from "../BackendFacade/IModuleInstances";
import React, {useEffect, useState} from "react";
import {IUserData} from "../Components/IDMS/Interfaces/IUsers";
import {IAuthLevel} from "../Components/IDMS/Interfaces/ITenantSystem";
import {OdxSelectOptionProps} from "../OdxComponents/NativeOdx/FormControls/Odx-Select";
import {HubPathRouting} from "../HubFramework/pathBuilder";


export function decodeQueryParam(p: string): string {
    return decodeURIComponent(p.replace(/\+/g, ' '));
}


export function isIdmsModuleMode(hubOrIdmsLevel) : boolean  {
    if (hubOrIdmsLevel === "IDMS") return true;
    else if (hubOrIdmsLevel === "Hub") return false;
    throw new Error("invalid usersTab mode: " + hubOrIdmsLevel);
}


export function removeDuplicates<TIn, TConv>(inputArray: TIn[], selectorFunction: (x: TIn) => TConv = null): TIn[] {
    if (!selectorFunction) selectorFunction = x => x;
    const selectedArray = inputArray.map(x => selectorFunction(x));
    return inputArray.filter(function (item, pos) {
        return item != null && selectedArray.indexOf(selectorFunction(item)) == pos;
    });
}

export function getIdFromUserObject(userObject: IUserData, isIdmsModule: boolean): string {
    if (isIdmsModule) return userObject.id;
    else return userObject.userId;
}

// HTML IDs dont allow certain characters. we remove them here and return a "clean" and valid HTML ID
// --> KEEP IN SYNC WITH FRONTEND TESTING!
export function simplifyForHtmlId(dirtyId) {
    if (dirtyId === undefined || dirtyId === null) {
        return 'undefined';
    }
    return String(dirtyId)
        .replace("@", "at")
        .replace(/\./g, "")
        .replace(/ /g,"")
        .replace("&","And")
        .replace(/\//g, "-");
}

export function getDisplayNameFromUserObject(userObject: IUserData): string {
    if (userObject.mail && userObject.mail.length) return userObject.mail;
    if (userObject.userId && userObject.userId.length) return userObject.userId;
    if (userObject.id && userObject.id.length) return userObject.id;
    return "";
}

export function getTenantObjectFromUserObject(userObject: IUserData, isIdmsModule: boolean): IAuthLevel {
    if (isIdmsModule) return JSON.parse(userObject.authLevel);
    else return userObject.tenantData;
}

export function formatDate (dateStr?: string)  {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr.replace(/\+00:00$/, 'Z'));
    return isNaN(date.getTime())
        ? 'N/A'
        : date.toLocaleString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: 'UTC'
        }).replace(',', '');
};

export function replaceProjectGuidWithDisplayName(scope: string): string {
    const instances: IProject[] = GlobalDictionary.get(GlobalDictionary.PROJECTS)
    if (instances === undefined) {
        return scope
    }
    return instances.find(x => x.projectId === scope)?.displayName ?? scope
}

/**
 * replaces the project guid with the display name
 * @param scopes scopes
 * @param position 1 = project, 2 = module
 * @param globalLayer if the scopes are global or not
 */
async function replaceGuidWithDisplayName(scopes: string[], position: number, globalLayer: boolean): Promise<string> {
    let returnValue = ""
    switch (position) {
        // replace project guid with display name
        case 1: {
            const instances: IProject[] = await GlobalDictionary.getProjects()
            if (instances === undefined) {
                returnValue = "[deleted]" // scopes[1]
                break;
            }
            returnValue = instances.find(x => x.projectId === scopes[1])?.displayName ?? "[deleted]" // scopes[1]
            break;
        }
        // replace module guid with display name
        case 2: {
            let modules: IModule[] = []
            if (globalLayer) {
                modules = GlobalDictionary.get(scopes[1] + GlobalDictionary.MODULEINSTANCES)
            } else {
                modules = GlobalDictionary.get(HubPathRouting.currentProject.id + GlobalDictionary.MODULEINSTANCES)
            }
            if (!modules) {
                returnValue = "[deleted]" // scopes[2]
                break;
            }
            returnValue = modules.find(x => x.moduleInstanceId === scopes[2])?.displayName ?? "[deleted]" // scopes[2]
            break;
        }
        default:
            returnValue = scopes[position]
    }
    // shorten the return value if it is too long
    if (returnValue.length > 15) {
        returnValue = returnValue.substring(0, 15) + "..."
    }
    return returnValue
}

export interface IGuidDisplayNameComponentProps {
    scopes: string[];
    position: number;
    globalLayer: boolean;

}

export const GuidDisplayNameComponent = ({scopes, position, globalLayer}: IGuidDisplayNameComponentProps) => {
    const [displayName, setDisplayName] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            const name = await replaceGuidWithDisplayName(scopes, position, globalLayer);
            setDisplayName(name);
        };
        fetchData();
    }, [scopes, position, globalLayer]);

    return (<>
            {displayName}
        </>
    );
};

/**
 * Converts a date to a format that the backend can understand
 * Converts from dd.mm.yyyy to yyyy-mm-ddT00:00:00.000Z
 * @param input
 */
export function formatDateToBackend(input: string): string {
    if (input === undefined || input === null) {
        return "";
    }
    const date = new Date(input);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${year}-${month}-${day}T00:00:00.000Z`;
}

/**
 * Deepcopy an object
 * @param obj
 */
export function deepCopy<TIn>(obj: TIn): TIn {
    return JSON.parse(JSON.stringify(obj));
}

/**
 * Remove empty spaces
 * @param pageName
 */
export function cleansePageName(pageName: string) {
    return pageName.replace(/\s/g, "");
}

export function convertListOfStringsToOptionsList(areas: string[]) {
    const options: OdxSelectOptionProps[] = [];
    if (areas == undefined) {
        return options;
    }
    areas.forEach((x) => {
        options.push({
            value: x,
            label: x
        })
    })
    return options;
}
